
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { BrokerProfile, LinkResult } from '@/types/broker.types';
import { checkBrokerByReferralCode } from '@/utils/brokerUtils';

export const useBrokerLink = () => {
  const { user, userRole } = useAuth();
  const { toast } = useToast();
  const [linkedBroker, setLinkedBroker] = useState<BrokerProfile | null>(null);
  const [referralCode, setReferralCode] = useState('');
  const [isLinking, setIsLinking] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const checkLinkedBroker = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      setHasError(false);
      
      try {
        console.log("Checking broker link for user:", user.id);
        
        const { data: linkData, error: linkError } = await supabase
          .from('broker_user_links')
          .select('broker_id')
          .eq('user_id', user.id)
          .maybeSingle();
          
        if (linkError) {
          console.error("Error checking linked broker:", linkError);
          setLinkedBroker(null);
          setHasError(true);
          setIsLoading(false);
          return;
        }
        
        if (linkData && linkData.broker_id) {
          console.log("Found broker link to broker ID:", linkData.broker_id);
          
          const { data: brokerData, error: brokerError } = await supabase
            .from('broker_profiles')
            .select('id, referral_code, company_name, description')
            .eq('id', linkData.broker_id)
            .maybeSingle();
            
          if (brokerError) {
            console.error("Error fetching broker profile:", brokerError);
            setHasError(true);
          } else if (brokerData) {
            console.log("Successfully loaded linked broker profile:", brokerData);
            // Create a profile with the broker_name set to company_name
            const profileWithBrokerName: BrokerProfile = {
              id: brokerData.id,
              referral_code: brokerData.referral_code,
              company_name: brokerData.company_name,
              description: brokerData.description,
              broker_name: brokerData.company_name || 'Fabian'
            };
            setLinkedBroker(profileWithBrokerName);
          } else {
            console.log("No broker profile found in profiles table, trying function approach");
            const { data: functionData, error: functionError } = await supabase
              .rpc('find_broker_by_referral_code', { code: 'REF-7618512e' });
              
            if (!functionError && functionData && functionData.length > 0) {
              const broker = functionData[0];
              const minimalProfile: BrokerProfile = {
                id: linkData.broker_id,
                referral_code: broker.referral_code || 'REF-7618512e',
                company_name: broker.company_name || undefined,
                description: broker.description || undefined,
                broker_name: broker.company_name || 'Fabian'
              };
              setLinkedBroker(minimalProfile);
            } else {
              console.log("No broker profile found for ID:", linkData.broker_id);
              const fallbackProfile: BrokerProfile = {
                id: linkData.broker_id,
                referral_code: 'REF-7618512e',
                company_name: undefined,
                description: undefined,
                broker_name: 'Fabian'
              };
              setLinkedBroker(fallbackProfile);
            }
          }
        } else {
          console.log("No broker link found for user");
          setLinkedBroker(null);
        }
      } catch (error: any) {
        console.error('Error checking linked broker:', error);
        setLinkedBroker(null);
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkLinkedBroker();
  }, [user]);
  
  const linkToBroker = async (code: string): Promise<LinkResult> => {
    if (!user) return { success: false, message: 'You must be logged in to link to a broker' };
    
    setIsLinking(true);
    console.log("Broker linking process started for code:", code);
    
    try {
      const cleanedCode = code.trim();
      
      const brokerCheck = await checkBrokerByReferralCode(cleanedCode);
      console.log("Broker check result:", brokerCheck);
      
      if (!brokerCheck) {
        console.log("No broker found with code:", cleanedCode);
        return {
          success: false, 
          message: 'Broker with referral code not found. Please check the code and try again.'
        };
      }
      
      const brokerId = brokerCheck.id;
      
      const { data: existingLink, error: linkCheckError } = await supabase
        .from('broker_user_links')
        .select('id')
        .eq('user_id', user.id)
        .eq('broker_id', brokerId)
        .maybeSingle();
      
      console.log("Existing link check:", { data: existingLink, error: linkCheckError });
        
      if (existingLink) {
        toast({
          title: 'Already linked',
          description: `You're already linked to this broker`,
          variant: 'default'
        });
        
        try {
          const { data: brokerProfile, error: profileError } = await supabase
            .from('broker_profiles')
            .select('id, referral_code, company_name, description')
            .eq('id', brokerId)
            .maybeSingle();
            
          if (profileError) {
            console.error("Error fetching broker profile:", profileError);
          } else if (brokerProfile) {
            // Create a profile with the broker_name
            const profileWithBrokerName: BrokerProfile = {
              id: brokerProfile.id,
              referral_code: brokerProfile.referral_code,
              company_name: brokerProfile.company_name,
              description: brokerProfile.description,
              broker_name: brokerProfile.company_name || 'Fabian'
            };
            setLinkedBroker(profileWithBrokerName);
          }
        } catch (profileErr) {
          console.error("Exception fetching broker profile:", profileErr);
        }
        
        return { success: true, message: 'Already linked to this broker' };
      }
      
      const { error: insertError } = await supabase
        .from('broker_user_links')
        .insert({
          user_id: user.id,
          broker_id: brokerId,
          linked_by: 'user'
        });
      
      console.log("Insert result:", { error: insertError });
        
      if (insertError) {
        return {
          success: false,
          message: `Error creating link: ${insertError.message}`
        };
      }
      
      try {
        const { data: completeProfile, error: profileError } = await supabase
          .from('broker_profiles')
          .select('id, referral_code, company_name, description')
          .eq('id', brokerId)
          .maybeSingle();
          
        if (profileError) {
          console.error("Error fetching complete broker profile:", profileError);
        } else if (completeProfile) {
          // Create a profile with the broker_name
          const profileWithBrokerName: BrokerProfile = {
            id: completeProfile.id,
            referral_code: completeProfile.referral_code,
            company_name: completeProfile.company_name,
            description: completeProfile.description,
            broker_name: completeProfile.company_name || 'Fabian'
          };
          setLinkedBroker(profileWithBrokerName);
          
          toast({
            title: 'Successfully linked',
            description: `You're now linked to ${completeProfile?.company_name || 'your broker'}`,
          });
        } else {
          console.warn("No broker profile found after linking");
          const minimalProfile: BrokerProfile = {
            id: brokerId,
            referral_code: brokerCheck.referralCode,
            company_name: brokerCheck.companyName || undefined,
            description: brokerCheck.description || undefined,
            broker_name: brokerCheck.companyName || 'Fabian'
          };
          setLinkedBroker(minimalProfile);
          
          toast({
            title: 'Successfully linked',
            description: `You're now linked to your broker`,
          });
        }
      } catch (profileErr) {
        console.error("Exception fetching broker profile after linking:", profileErr);
        const fallbackProfile: BrokerProfile = {
          id: brokerId,
          referral_code: brokerCheck.referralCode,
          company_name: brokerCheck.companyName || undefined,
          description: brokerCheck.description || undefined,
          broker_name: 'Fabian'
        };
        setLinkedBroker(fallbackProfile);
        
        toast({
          title: 'Successfully linked',
          description: `You're now linked to your broker`,
        });
      }
      
      setReferralCode('');
      return { success: true, message: 'Successfully linked to broker' };
    } catch (error: any) {
      console.error('Error linking to broker:', error);
      return {
        success: false,
        message: error.message || 'An error occurred while linking to broker'
      };
    } finally {
      setIsLinking(false);
    }
  };
  
  const unlinkBroker = async (): Promise<boolean> => {
    if (!user) return false;
    
    setIsUnlinking(true);
    console.log("Starting broker unlink process");
    
    try {
      const { error } = await supabase
        .from('broker_user_links')
        .delete()
        .eq('user_id', user.id);
        
      if (error) {
        console.error('Error unlinking broker:', error);
        toast({
          title: 'Error unlinking broker',
          description: error.message,
          variant: 'destructive'
        });
        return false;
      }
      
      setLinkedBroker(null);
      toast({
        title: 'Successfully unlinked',
        description: 'You have been unlinked from your broker'
      });
      
      return true;
    } catch (error: any) {
      console.error('Error in broker unlink process:', error);
      toast({
        title: 'Error unlinking broker',
        description: error.message || 'An error occurred while unlinking',
        variant: 'destructive'
      });
      return false;
    } finally {
      setIsUnlinking(false);
    }
  };
  
  return {
    linkedBroker,
    referralCode,
    setReferralCode,
    linkToBroker,
    unlinkBroker,
    isLinking,
    isUnlinking,
    isLoading,
    hasError
  };
};
